/** Independent of reporting dates. Observations never authorize a write. */
export type AdsManagerDeliveryObservations = {
  brandId: string;
  accountId: string;
  source: "windsor";
  state: "ready" | "waiting" | "unavailable";
  observedAt: string | null;
  retryAt: string | null;
  statuses: Record<string, string>;
};

export type AdsManagerDeliveryObservation = {
  source: "windsor";
  observedAt: string;
  stale: boolean;
};

export function applyDeliveryObservations<T extends {
  id: string; level: string; configuredStatus: string; effectiveStatus: string;
  accountId?: string; deliveryReadiness?: "recent_launch_only" | "provider_observed" | "historical_reporting";
  recentLaunchStatusRecovery?: boolean;
  children?: T[]; deliveryStatusObservation?: AdsManagerDeliveryObservation;
}>(
  rows: T[], evidence: AdsManagerDeliveryObservations | undefined,
  scope: { brandId: string; accountId: string }, now = Date.now(),
): T[] {
  if (!evidence || evidence.brandId !== scope.brandId || evidence.accountId !== scope.accountId
    || !evidence.observedAt || !Number.isFinite(Date.parse(evidence.observedAt))
    || Date.parse(evidence.observedAt) > now + 5_000) return rows;
  const stale = evidence.state !== "ready" || now - Date.parse(evidence.observedAt) >= 60_000;
  return rows.map(row => {
    const configuredStatus = !row.accountId || row.accountId.replace(/^act_/i, "") === scope.accountId
      ? evidence.statuses[`${row.level}:${row.id}`] : undefined;
    const recovered = row.level === "campaign" && (row.deliveryReadiness === "recent_launch_only"
      || row.deliveryReadiness === "historical_reporting" && row.recentLaunchStatusRecovery === true)
      && !stale && (configuredStatus === "ACTIVE" || configuredStatus === "PAUSED");
    return {
      ...row,
      ...(configuredStatus ? {
        configuredStatus,
        // Windsor exposes configured status, not Graph effective delivery.
        effectiveStatus: "UNKNOWN",
        deliveryStatusObservation: { source: "windsor" as const, observedAt: evidence.observedAt!, stale },
        ...(recovered ? { deliveryReadiness: "provider_observed" as const } : {}),
      } : {}),
      ...(row.children ? { children: applyDeliveryObservations(row.children, evidence, scope, now) } : {}),
    };
  });
}
